import React, { useState } from "react";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import { Button } from "@mui/material";
import moment, { Moment } from "moment-timezone";

interface GraphTimePickerProps {
  startDateTime: Moment;
  endDateTime: Moment;
  setStartDateTime: (date: Moment) => void;
  setEndDateTime: (date: Moment) => void;
  selectedRange: string;
  setSelectedRange: (label: string) => void;
}

const timeRanges = [
  { label: "Last 6 hours", hours: 6 },
  { label: "Last 12 hours", hours: 12 },
  { label: "Last 24 hours", hours: 24 },
  { label: "Yesterday", hours: null },
  { label: "Last 3 days", hours: 72 },
  { label: "Last 7 days", hours: 168 },
  { label: "Last 30 days", hours: 720 },
  { label: "Custom", hours: null }
];

const GraphTimePicker: React.FC<GraphTimePickerProps> = ({
  startDateTime,
  endDateTime,
  setStartDateTime,
  setEndDateTime,
  selectedRange,
  setSelectedRange
}) => {
  const [showCustomPickers, setShowCustomPickers] = useState<boolean>(false);
  const [localCustomStart, setLocalCustomStart] = useState<Moment | null>(null);
  const [localCustomEnd, setLocalCustomEnd] = useState<Moment | null>(null);

  // Limit custom selection to the last 1 month until now in America/New_York
  const tz = 'America/New_York';
  const nowTz = moment().tz(tz);
  const maxSelectableDateTime = nowTz.clone();
  const minSelectableDateTime = nowTz.clone().subtract(1, 'month');

  const updateTimeRange = (hours: number) => {
    // Create a single reference point in the current timezone
    const now = moment().tz('America/New_York');
    const newStart = now.clone().subtract(hours, "hour");
    const newEnd = now.clone();
    
    // Update both times atomically
    setStartDateTime(newStart);
    setEndDateTime(newEnd);
  };

  const handleRangeChange = (event: SelectChangeEvent<string>) => {
    const selected = event.target.value;
    const range = timeRanges.find((r) => r.label === selected);
    
    if (range) {
      setSelectedRange(selected);
      if (range.label === "Custom") {
        setShowCustomPickers(true);
        // Initialize local custom values to current external values
        setLocalCustomStart(startDateTime.clone());
        setLocalCustomEnd(endDateTime.clone());
      } else if (range.label === "Yesterday") {
        setShowCustomPickers(false);
        const tz = 'America/New_York';
        const todayStart = moment().tz(tz).startOf('day');
        const start = todayStart.clone().subtract(1, 'day');
        const end = todayStart.clone();
        setStartDateTime(start);
        setEndDateTime(end);
      } else if (range.hours !== null) {
        setShowCustomPickers(false);
        updateTimeRange(range.hours);
      }
    }
  };

  const handleStartDateTimeChange = (newValue: Moment | null) => {
    if (newValue) {
      const tzValue = newValue.clone().tz('America/New_York');
      setLocalCustomStart(tzValue);
    }
  };

  const handleEndDateTimeChange = (newValue: Moment | null) => {
    if (newValue) {
      const tzValue = newValue.clone().tz('America/New_York');
      setLocalCustomEnd(tzValue);
    }
  };

  const applyCustomRange = () => {
    if (localCustomStart && localCustomEnd) {
      // Clamp within allowed bounds as a safeguard
      const clampedStart = moment.max(minSelectableDateTime, moment.min(localCustomStart, maxSelectableDateTime));
      const clampedEnd = moment.max(minSelectableDateTime, moment.min(localCustomEnd, maxSelectableDateTime));
      setStartDateTime(clampedStart.clone());
      setEndDateTime(clampedEnd.clone());
    }
  };

  return (
    <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
      <FormControl size="small">
        <InputLabel id="time-range-label">Time Range</InputLabel>
        <Select
          labelId="time-range-label"
          value={selectedRange}
          onChange={handleRangeChange}
          label="Time Range"
        >
          {timeRanges.map((range) => (
            <MenuItem key={range.label} value={range.label}>
              {range.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showCustomPickers && (
        <>
          <DateTimePicker
            label="Start DateTime"
            value={localCustomStart ?? startDateTime}
            onChange={handleStartDateTimeChange}
            minDateTime={minSelectableDateTime}
            maxDateTime={maxSelectableDateTime}
          />
          <DateTimePicker
            label="End DateTime"
            value={localCustomEnd ?? endDateTime}
            onChange={handleEndDateTimeChange}
            minDateTime={minSelectableDateTime}
            maxDateTime={maxSelectableDateTime}
          />
          <Button
            variant="contained"
            size="small"
            onClick={applyCustomRange}
            disabled={!localCustomStart || !localCustomEnd}
          >
            Apply
          </Button>
        </>
      )}
    </div>
  );
};

export default GraphTimePicker;



